import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'

pdfMake.addVirtualFileSystem(pdfFonts)

// ─── Paleta de cores ───────────────────────────────────────────────────────
const COLORS = {
  primary: '#0F172A',
  accent: '#3B82F6',
  accentLight: '#DBEAFE',
  surface: '#F8FAFC',
  border: '#E2E8F0',
  muted: '#64748B',
  white: '#FFFFFF',
  rowAlt: '#F1F5F9',
  positive: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
}

// ─── Helpers ───────────────────────────────────────────────────────────────
function ddeColor(dde) {
  const v = parseFloat(dde)
  if (v === 0 || v <= 7) return COLORS.danger
  if (v <= 30) return COLORS.warning
  return COLORS.positive
}

function headerCell(text) {
  return {
    text,
    fontSize: 7.5,
    bold: true,
    color: COLORS.white,
    fillColor: COLORS.primary,
    margin: [6, 5, 6, 5],
    alignment: 'center',
  }
}

function dataCell(text, opts = {}) {
  return {
    text: String(text),
    fontSize: 8,
    color: opts.color || COLORS.primary,
    fillColor: opts.fillColor || COLORS.white,
    margin: [6, 4, 6, 4],
    alignment: opts.align || 'center',
    bold: opts.bold || false,
  }
}

function skuCell(text, fillColor) {
  return {
    text: String(text),
    fontSize: 7.5,
    color: COLORS.accent,
    fillColor,
    margin: [6, 4, 6, 4],
    alignment: 'center',
    bold: true,
  }
}

function descCell(text, fillColor) {
  return {
    text: String(text),
    fontSize: 8,
    color: COLORS.primary,
    fillColor,
    margin: [6, 4, 6, 4],
    alignment: 'left',
  }
}

// ─── Cards de resumo ───────────────────────────────────────────────────────
function buildSummary(data) {
  const totalVendas = data.reduce((s, i) => s + Number(i.VENDA || 0), 0)
  const totalEstoque = data.reduce((s, i) => s + Number(i.ESTOQUE || 0), 0)
  const semEstoque = data.filter((i) => Number(i.ESTOQUE) === 0).length

  const card = (label, value, accent) => ({
    stack: [
      {
        text: String(value),
        fontSize: 18,
        bold: true,
        color: accent,
        margin: [0, 0, 0, 2],
      },
      { text: label, fontSize: 7.5, color: COLORS.muted },
    ],
  })

  return {
    columns: [
      card('Total de Produtos', data.length, COLORS.accent),
      card('Total de Vendas', totalVendas, COLORS.positive),
      card('Total em Estoque', totalEstoque, COLORS.primary),
      card('Sem Estoque', semEstoque, COLORS.danger),
    ],
    columnGap: 24,
    margin: [0, 0, 0, 16],
  }
}

// ─── Tabela principal ──────────────────────────────────────────────────────
function buildTable(data) {
  const headers = [
    headerCell('SKU'),
    headerCell('PRODUTO'),
    headerCell('VENDAS'),
    headerCell('ESTOQUE'),
    headerCell('MD VENDA'),
    headerCell('DDE (dias)'),
  ]

  const rows = data.map((item, idx) => {
    const fill = idx % 2 === 0 ? COLORS.white : COLORS.rowAlt
    const dde =
      item.ESTOQUE !== 0 && item.MD_VENDA !== 0
        ? (item.ESTOQUE / item.MD_VENDA).toFixed(1)
        : '0.0'

    return [
      skuCell(item.CODPROD, fill),
      descCell(item.DESCRICAO, fill),
      dataCell(item.VENDA, { fillColor: fill }),
      dataCell(item.ESTOQUE, { fillColor: fill }),
      dataCell(item.MD_VENDA.toFixed(2), { fillColor: fill }),
      {
        text: dde,
        fontSize: 8,
        bold: true,
        color: ddeColor(dde),
        fillColor: fill,
        margin: [6, 4, 6, 4],
        alignment: 'center',
      },
    ]
  })

  return {
    table: {
      headerRows: 1,
      widths: [52, '*', 46, 46, 50, 58],
      body: [headers, ...rows],
    },
    layout: {
      hLineWidth: (i, node) =>
        i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.4,
      vLineWidth: () => 0,
      hLineColor: (i) => (i === 0 || i === 1 ? COLORS.primary : COLORS.border),
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
  }
}

// ─── Legenda DDE ───────────────────────────────────────────────────────────
function buildLegend() {
  const dot = (color, label) => ({
    columns: [
      {
        canvas: [{ type: 'ellipse', x: 5, y: 5, r1: 4, r2: 4, color }],
        width: 14,
      },
      {
        text: label,
        fontSize: 7.5,
        color: COLORS.muted,
        margin: [0, 1, 0, 0],
      },
    ],
    columnGap: 4,
    width: 'auto',
  })

  return {
    columns: [
      {
        text: 'Legenda DDE:',
        fontSize: 7.5,
        bold: true,
        color: COLORS.primary,
        margin: [0, 1, 8, 0],
        width: 'auto',
      },
      dot(COLORS.danger, '≤ 7 dias (crítico)'),
      dot(COLORS.warning, '8 – 30 dias (atenção)'),
      dot(COLORS.positive, '> 30 dias (ok)'),
    ],
    columnGap: 12,
    margin: [0, 10, 0, 0],
  }
}

// ─── Função principal ──────────────────────────────────────────────────────
async function gerarRelatorioUltimosDias(daysOfSales, data) {
  console.log(data)

  const currentDate = new Date().toLocaleString('pt-BR')
  const username =
    (typeof localStorage !== 'undefined' && localStorage.username) || 'usuário'

  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [32, 100, 32, 60],

    // ── Cabeçalho ──────────────────────────────────────────────────────────
    header: {
      stack: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 595, h: 6, color: COLORS.accent },
          ],
        },
        {
          columns: [
            {
              stack: [
                {
                  text: 'RELATÓRIO DE VENDAS',
                  fontSize: 16,
                  bold: true,
                  color: COLORS.primary,
                  characterSpacing: 1.5,
                },
                {
                  text: `Últimos ${daysOfSales} dias`,
                  fontSize: 9,
                  color: COLORS.accent,
                  margin: [0, 2, 0, 0],
                },
              ],
              margin: [32, 14, 0, 0],
            },
            {
              stack: [
                {
                  text: currentDate,
                  fontSize: 8,
                  color: COLORS.muted,
                  alignment: 'right',
                },
                {
                  text: `Usuário: ${username}`,
                  fontSize: 8,
                  color: COLORS.muted,
                  alignment: 'right',
                  margin: [0, 2, 0, 0],
                },
                {
                  text: `${data.length} produtos`,
                  fontSize: 8,
                  color: COLORS.accent,
                  bold: true,
                  alignment: 'right',
                  margin: [0, 2, 0, 0],
                },
              ],
              margin: [0, 14, 32, 0],
            },
          ],
        },
        {
          canvas: [
            {
              type: 'line',
              x1: 32,
              y1: 0,
              x2: 563,
              y2: 0,
              lineWidth: 0.5,
              lineColor: COLORS.border,
            },
          ],
          margin: [0, 10, 0, 0],
        },
      ],
    },

    // ── Rodapé ─────────────────────────────────────────────────────────────
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: 'Documento gerado automaticamente — uso interno',
          fontSize: 7,
          color: COLORS.muted,
          margin: [32, 10, 0, 0],
        },
        {
          text: `Página ${currentPage} de ${pageCount}`,
          fontSize: 7,
          color: COLORS.muted,
          alignment: 'right',
          margin: [0, 10, 32, 0],
        },
      ],
    }),

    // ── Conteúdo ───────────────────────────────────────────────────────────
    content: [
      buildSummary(data),
      {
        canvas: [
          { type: 'rect', x: 0, y: 0, w: 531, h: 2, color: COLORS.accentLight },
        ],
        margin: [0, 0, 0, 8],
      },
      buildTable(data),
      buildLegend(),
    ],

    defaultStyle: {
      font: 'Roboto',
    },
  }

  pdfMake.createPdf(docDefinition).open()
}

export { gerarRelatorioUltimosDias }
